import { type IDBPDatabase, type IDBPObjectStore, type IDBPTransaction, openDB } from 'idb'

import type { SynopIndexedDbSchema, SynopIndexedDbStoreName } from './schema'

type StoreName = SynopIndexedDbStoreName
type StoreValue<Store extends StoreName> = SynopIndexedDbSchema[Store]['value']
type StoreKey<Store extends StoreName> = SynopIndexedDbSchema[Store]['key']

export type StoreDefinition = {
  name: StoreName
  options?: IDBObjectStoreParameters
}

type TransactionMode = 'readonly' | 'readwrite'
type Transaction<Stores extends StoreName[], Mode extends TransactionMode = TransactionMode> = IDBPTransaction<
  SynopIndexedDbSchema,
  Stores,
  Mode
>
type SingleStoreWriteTransaction<Store extends StoreName> = Transaction<[Store], 'readwrite'>

type IndexedDbServiceOptions = {
  /**
   * Database name
   */
  name: string
  /**
   * The current version of the database schema. Increment the version whenever the schema changes
   * (e.g., new store, new index).
   */
  version: number
  /**
   * Store definitions used during database creation and version upgrades
   */
  stores: StoreDefinition[]
}

/**
 * Centralized service for IndexedDB access. It hides the boilerplate of opening the database,
 * managing upgrades, and notifying subscribers after writes.
 */
export class IndexedDbService {
  private dbPromise: Promise<IDBPDatabase<SynopIndexedDbSchema>> | null = null
  private db: IDBPDatabase<SynopIndexedDbSchema> | null = null
  private readonly options: IndexedDbServiceOptions

  constructor(options: IndexedDbServiceOptions) {
    this.options = options
  }

  /**
   * Opens or returns an existing database connection. Lazily initializes the connection on first
   * call and caches it for subsequent calls.
   */
  private getDb(): Promise<IDBPDatabase<SynopIndexedDbSchema>> {
    const { name, version, stores } = this.options

    if (!this.dbPromise) {
      this.dbPromise = openDB<SynopIndexedDbSchema>(name, version, {
        upgrade: async (db) => {
          // Build a set of store names that should exist according to current config
          const definedStoreNames = new Set(stores.map((store) => store.name))

          // Create any stores that are defined but don't yet exist in the database.
          // Note: This does NOT update existing stores if their options (keyPath, etc.) change.
          for (const store of stores) {
            if (!db.objectStoreNames.contains(store.name)) {
              db.createObjectStore(store.name, store.options)
            }
          }

          // Remove any stores that exist in the database but are no longer defined.
          // This cleans up deprecated stores when the schema evolves.
          for (const existingStoreName of Array.from(db.objectStoreNames)) {
            if (!definedStoreNames.has(existingStoreName as SynopIndexedDbStoreName)) {
              db.deleteObjectStore(existingStoreName)
            }
          }
        },
        // Another connection — typically the same app open in a different tab — is
        // upgrading or deleting this database. IndexedDB blocks that request until
        // every open connection closes, and a blocked delete/upgrade in turn leaves
        // every subsequent open() queued indefinitely. Release our connection so the
        // request can proceed; the next access reopens lazily.
        blocking: () => this.close(),
        // The browser closed the connection out from under us (e.g. a discarded tab).
        // Drop the cached handles so the next access reopens.
        terminated: () => {
          this.db = null
          this.dbPromise = null
        }
      }).then((db) => {
        // Keep a direct handle so `blocking` can close synchronously. Awaiting the
        // promise there would yield to the event loop and risk the upgrade/delete
        // request blocking before we manage to close.
        this.db = db
        return db
      })
    }

    return this.dbPromise
  }

  /**
   * Closes the cached connection (if any) and clears the cached handles so the next
   * access reopens. Releasing the connection is what lets another tab's database
   * upgrade or deletion proceed instead of blocking — a blocked delete/upgrade
   * otherwise freezes every subsequent open() on the database.
   */
  close() {
    const db = this.db
    const dbPromise = this.dbPromise
    this.db = null
    this.dbPromise = null

    if (db) {
      db.close()
    } else if (dbPromise) {
      void dbPromise.then((openDb) => openDb.close())
    }
  }

  /**
   * Executes an operation within a transaction, automatically committing on success or aborting
   * on error.
   */
  private async runInTransaction<Stores extends StoreName[], Mode extends TransactionMode, Result>(
    storeNames: Stores,
    mode: Mode,
    executor: (tx: Transaction<Stores, Mode>) => Promise<Result>
  ) {
    const db = await this.getDb()
    const tx = db.transaction(storeNames, mode)

    const result = await executor(tx)
    await tx.done
    return result
  }

  /**
   * Executes a write operation on a single store. Uses an existing transaction if provided,
   * otherwise creates a new one.
   */
  private async withSingleStoreWrite<Store extends StoreName, Result>(
    storeName: Store,
    operation: (store: IDBPObjectStore<SynopIndexedDbSchema, [Store], Store, 'readwrite'>) => Promise<Result>,
    tx?: SingleStoreWriteTransaction<Store>
  ) {
    if (tx) {
      return operation(tx.objectStore(storeName))
    }

    return this.runInTransaction<[Store], 'readwrite', Result>([storeName], 'readwrite', async (transaction) =>
      operation(transaction.objectStore(storeName))
    )
  }

  /**
   * Retrieves a single record by key from the specified store.
   */
  async get<Store extends StoreName>(storeName: Store, key: StoreKey<Store>) {
    const db = await this.getDb()
    return db.get(storeName, key)
  }

  /**
   * Retrieves all records from the specified store.
   */
  async getAll<Store extends StoreName>(storeName: Store) {
    const db = await this.getDb()
    return db.getAll(storeName)
  }

  /**
   * Inserts or updates a single record in the specified store.
   */
  put<Store extends StoreName>(storeName: Store, value: StoreValue<Store>, tx?: SingleStoreWriteTransaction<Store>) {
    return this.withSingleStoreWrite(storeName, (store) => store.put(value), tx)
  }

  /**
   * Inserts or updates multiple records in the specified store.
   */
  putMany<Store extends StoreName>(
    storeName: Store,
    records: StoreValue<Store>[],
    tx?: SingleStoreWriteTransaction<Store>
  ) {
    if (!records.length) return Promise.resolve([])
    return this.withSingleStoreWrite(storeName, (store) => Promise.all(records.map((record) => store.put(record))), tx)
  }

  /**
   * Deletes a single record by key from the specified store.
   */
  delete<Store extends StoreName>(storeName: Store, key: StoreKey<Store>, tx?: SingleStoreWriteTransaction<Store>) {
    return this.withSingleStoreWrite(storeName, (store) => store.delete(key), tx)
  }

  /**
   * Removes all records from the specified store.
   */
  clear<Store extends StoreName>(storeName: Store, tx?: SingleStoreWriteTransaction<Store>) {
    return this.withSingleStoreWrite(storeName, (store) => store.clear(), tx)
  }

  /**
   * Atomically replaces all records in a store with the provided records. Clears the store first,
   * then inserts the new records within a single transaction.
   */
  replace<Store extends StoreName>(storeName: Store, records: StoreValue<Store>[]) {
    return this.runInTransaction<[Store], 'readwrite', void>([storeName], 'readwrite', async (tx) => {
      await this.clear(storeName, tx)
      if (records.length) {
        await this.putMany(storeName, records, tx)
      }
    })
  }
}
